import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {WebSocketResponse} from '../../types/websocket.types';
import {NbAuthService} from '@nebular/auth';

@Injectable({
  providedIn: 'root',
})
export abstract class WebSocketService implements OnDestroy {
  messages: Subject<WebSocketResponse>;
  subscriptionsList = [];
  private readonly wsUrl: any;
  private readonly authService: NbAuthService;
  private worker: Worker;
  protected disconnectionSubject = new BehaviorSubject(false);
  protected reconnectModalSubject = new BehaviorSubject(false);

  protected constructor(authService: NbAuthService, wsUrl: string) {
    this.authService = authService;
    this.wsUrl = wsUrl;
    authService.onTokenChange().subscribe(token => {
      const localToken = token.getValue();
      if (localToken && this.worker) {
        const localUrl = `${this.wsUrl}?access_token=${localToken}`;
        this.worker.postMessage({action: 'update_url', url: localUrl});
      }
    });
  }

  ngOnDestroy() {
    this.destroyConnection();
  }

  public initConnection() {
    if (!this.worker) {
      this.worker = new Worker(new URL('./websocket-worker.worker', import.meta.url), {type: 'module'});
      this.messages = new Subject<any>();
      const token = localStorage.getItem('auth_app_token') ? JSON.parse(localStorage.getItem('auth_app_token')).value : '';
      if (token) {
        this.wsCreate(this.wsUrl, token);
      } else {
        console.log("No JWT Token present when trying to create WebSocket connection.");
      }
    }
  }

  public destroyConnection() {
    try {
      if (this.worker) {
        this.close();
      }
    } catch (error) {
      console.error("Could not close connection", error);
    } finally {
      console.log('closed connections');
    }
  }

  openWM() {
    if (this.worker) {
      this.worker.onmessage = (event) => {
        switch (event.data.type) {
          case 'open': {
            // will close the reconnection modal in case it was opened before
            this.setReconnectModalSubject(false);

            this.subscriptionsList.forEach((elem) => {
              this.send(JSON.stringify(elem));
            });
            break;
          }
          case 'message': {
            if (event.data?.data) {
              this.messages.next(event.data.data);
            } else {
              this.messages.next(event.data);
            }
            break;
          }
          case 'close': {
            console.log("Disconnected...");
            break;
          }
          case 'attempt_reconnect':
            // will open a modal informing the user that he lost connection and we are trying to reconnect
            this.setReconnectModalSubject(true);

            const token = localStorage.getItem('auth_app_token') ?
              JSON.parse(localStorage.getItem('auth_app_token')).value : '';
            if (token) {
              const localUrl = this.getUrl(this.wsUrl, token);
              this.worker.postMessage({action: 'update_url', url: localUrl});
            } else {
              this.setDisconnectionSubject(true);
            }
            break;
          case 'reconnect_failed':
            this.setDisconnectionSubject(true);
            break;
        }
      };
    }
  }

  close() {
    this.send(JSON.stringify({type: 'close'}));
    this.worker.postMessage({action: 'close'});
    this.worker.terminate();
    this.worker = null;
  }

  private getUrl(url, token): string {
    const access_token = JSON.parse(token).access_token;
    return url + '?access_token=' + access_token;
  }

  private wsCreate(url, token: any): void {
    const local_url = this.getUrl(url, token);
    this.openWM();
    this.worker.postMessage({action: 'connect', url: local_url});
  }

  subscribe(message: any) {
    this.subscriptionsList.push(message);
    this.send(JSON.stringify(message));
  }

  unsubscribe(message: any) {
    const removeSubscription = this.subscriptionsList.find((subs) => {
      return subs.type === 'subscribe' && subs.topic === message.topic;
    });

    this.subscriptionsList = this.subscriptionsList.filter((subs) => {
      return subs !== removeSubscription;
    });

    this.send(JSON.stringify(message));
  }

  send(message: any) {
    if (this.worker) {
      this.worker.postMessage({action: 'send', message});
    }
  }

  getDisconnectionSubject() {
    return this.disconnectionSubject;
  }

  setDisconnectionSubject(value: boolean) {
    return this.disconnectionSubject.next(value);
  }

  getReconnectModalSubject() {
    return this.reconnectModalSubject;
  }

  setReconnectModalSubject(value: boolean) {
    return this.reconnectModalSubject.next(value);
  }

}
